<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useInboxCopy } from '@/components/inbox/useInboxCopy'
import AppButton from '@/components/primitives/AppButton/AppButton.vue'
import AppNotice from '@/components/primitives/AppNotice/AppNotice.vue'
import type { InboxThread } from '@/types/inbox'

import { FOR_YOU_NOTICE_MS } from './inbox-notices'

const props = defineProps<{
  /** The thread the notice announces. */
  thread: InboxThread
}>()

const emit = defineEmits<{
  open: []
  later: []
  expire: []
}>()

const { t } = useI18n()
const copy = useInboxCopy()

const important = computed(() => props.thread.actionNeeded)
const held = ref(false)
let timer: ReturnType<typeof setTimeout> | null = null

function stopTimer(): void {
  if (timer !== null) clearTimeout(timer)
  timer = null
}

function startTimer(): void {
  stopTimer()
  if (important.value || held.value) return
  timer = setTimeout(() => emit('expire'), FOR_YOU_NOTICE_MS)
}

function hold(value: boolean): void {
  held.value = value
  startTimer()
}

/** Opens the thread when the whole card is the target, which is the case for a notice without actions. */
function activate(): void {
  if (!important.value) emit('open')
}

onMounted(startTimer)
onBeforeUnmount(stopTimer)
</script>

<template>
  <div
    :class="important ? undefined : 'cursor-pointer'"
    :role="important ? undefined : 'button'"
    :tabindex="important ? undefined : 0"
    @click="activate"
    @keydown.enter="activate"
    @mouseenter="hold(true)"
    @mouseleave="hold(false)"
    @focusin="hold(true)"
    @focusout="hold(false)"
  >
    <AppNotice
      shape="toast"
      :tone="important ? 'warning' : 'neutral'"
      :title="copy.subject(thread.latest)"
      :message="copy.line(thread.latest)"
    >
      <template v-if="important" #actions>
        <AppButton
          size="xs"
          variant="primary"
          :label="copy.verb(thread.latest)"
          @click="emit('open')"
        />
        <AppButton size="xs" variant="ghost" :label="t('inbox.later')" @click="emit('later')" />
      </template>
    </AppNotice>
  </div>
</template>
